import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "../../_css/ChatPopup.module.css";
import {
  X,
  FileText,
  Waves,
  ArrowUp,
  Copy,
  Plus,
  AudioLines,
  RotateCcw,
  PhoneOff,
} from "lucide-react";
import SessionActions from "../Modals/SessionActions";
import { useDispatch, useSelector } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { deleteAuthToken, notifyError } from "../../utils/helper";
import {
  finalizeTraining,
  getTrainingSummary,
  startChat,
  startTraining,
} from "../../requests";
import ResponseLoader from "../common/ResponseLoader";
import { refreshSessionData } from "../../_store/reducers/refreshDataReducer";
import { isHTML, formatHTMLResponse } from "../helper/formatters";
import { notifySuccess } from "../../utils/helper";

const AI_AVATAR = `${process.env.PUBLIC_URL}/img/icon-ai-l1.png`;
const DR_AVATAR = `${process.env.PUBLIC_URL}/img/dr-salah-icon.png`;

const ChatPopup = ({
  show,
  onClose,
  from = null,
  chatData = { chatMsgs: [], isSummary: false },
}) => {
  const [messages, setMessages] = useState(chatData.chatMsgs);
  const [input, setInput] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [hovered, setHovered] = useState("");
  const [showSaveSession, setShowSaveSession] = useState(false);
  const [summary, setSummary] = useState(chatData.isSummary);
  const [timer, setTimer] = useState("15:00");
  const [textareaHeight, setTextareaHeight] = useState("auto");

  const { reviewSessionTitle } = useSelector((state) => state.reviewSession);

  const dispatch = useDispatch();

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const sessionId = sessionStorage.getItem("trainerSessionId");
  const trainingLanguage = sessionStorage.getItem("trainingLanguage");

  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [messages]);

  // Start Chat on Component Mount only Triggered Once
  React.useEffect(() => {
    if (show && chatData.chatMsgs.length === 0) {
      handleStartChat();
    } else if (show && chatData.chatMsgs.length > 0) {
      setMessages(chatData.chatMsgs);
      setSummary(chatData.isSummary);
    } else {
      resetStates();
    }
    return () => {
      resetStates();
    };
  }, [show]);

  const handleStartChat = async () => {
    setWaiting(true);
    try {
      const response = await startTraining({
        sessionId,
        language_code: trainingLanguage,
      });
      // Trigger a refresh of the current page to get updated data
      const aiText = response.data.message;
      setMessages([{ sender: "ai", text: aiText }]);
      setInput("");
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Initiating Chat:", error);
    }
    setWaiting(false);
  };

  const resetStates = () => {
    setMessages([]);
    setInput("");
    setWaiting(false);
    setHovered("");
    setShowSaveSession(false);
    setSummary(false);
    setTimer("15:00");
    setTextareaHeight("auto");
  };

  // Auto-resize textarea function
  const handleTextareaChange = (e) => {
    const textarea = e.target;
    setInput(textarea.value);
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";
    
    // Calculate new height (min 40px, max 200px)
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 40), 200);
    textarea.style.height = newHeight + "px";
    setTextareaHeight(newHeight + "px");
    
    // If content exceeds max height, ensure scrolling is enabled
    if (textarea.scrollHeight > 200) {
      textarea.style.overflowY = "auto";
    } else {
      textarea.style.overflowY = "hidden";
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    // to regenerate summary
    if (summary) {
      setSummary(false);
    }
    setMessages((msgs) => [...msgs, { sender: "dr", text: input }]);
    setInput("");
    setTextareaHeight("auto");
    setWaiting(true);
    const aiText = await handleChat(input);
    setMessages((msgs) => [...msgs, { sender: "ai", text: aiText }]);
    setWaiting(false);
    // setTimeout(() => {
    //   const aiText =
    //     aiResponses[Math.floor(Math.random() * aiResponses.length)];
    //   setMessages((msgs) => [...msgs, { sender: "ai", text: aiText }]);
    //   setWaiting(false);
    //   setTimeout(() => {
    //     chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    //   }, 100);
    // }, 1500 + Math.random() * 1000);
  };

  const handleChat = async (userMessage) => {
    try {
      const response = await startChat({ sessionId, user_msg: userMessage });
      const aiText = response.data.message;

      return aiText;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
  };

  const handleOpenSaveSession = () => {
    setShowSaveSession(true);
    
  };

  const generateSummary = async (userMessage) => {
    try {
      const response = await getTrainingSummary({ sessionId });
      const aiSummary = response.data.message;

      return aiSummary;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
  };

  const handleGenerateSummary = async () => {
    setInput("");
    setWaiting(true);
    setShowSaveSession(false);
    const aiSummary = await generateSummary();
    setMessages((msgs) => [
      ...msgs,
      { sender: "ai", text: aiSummary, summary: true },
    ]);
    // setTimeout(() => {
    //   setMessages((msgs) => [...msgs, ...summaryMessages]);
    //   setWaiting(false);
    //   setTimeout(() => {
    //     chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    //   }, 100);
    // }, 1500 + Math.random() * 1000);
    setWaiting(false);
    setSummary(true);
  };

  const handleFinalizeSession = async (isPublish = false, isDraft = 0) => {
    console.log("handleFinalizeSession");
    dispatch(startLoader());
    try {
      await finalizeTraining({ sessionId, isPublish, isDraft });
     
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      notifyError("Something went wrong. Please try again later.");
      console.error("Error Sending Message:", error);
    }
    dispatch(stopLoader());
    setShowSaveSession(false);
    if (isPublish || isDraft) {
      onClose();
      dispatch(refreshSessionData());
    }
  };

  return (
    <>
      {show && (
        <div className={styles.overlay}>
          <div className={styles.popup}>
            <div className={styles.header}>
              <h4 className={styles.title}>
                {reviewSessionTitle}
              </h4>
              <button className={styles.closeBtn} onClick={onClose}>
                <X size={22} />
              </button>
            </div>
            <div className={styles.chatBody}>
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={
                    msg.sender === "ai"
                      ? from === "reviewSession"
                        ? styles.drMsg
                        : styles.aiMsg
                      : from === "reviewSession"
                      ? styles.aiMsg
                      : styles.drMsg
                  }
                >
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      <i>
                        <img
                          src={msg.sender === "ai" ? AI_AVATAR : DR_AVATAR}
                          alt="avatar"
                        />
                      </i>

                      <span>{msg.sender === "ai" ? "AI" : "Salah"}</span>
                    </div>
                    {msg?.summary && <strong>Summary Generated:</strong>}

                    {isHTML(msg.text) ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formatHTMLResponse(msg.text),
                        }}
                      />
                    ) : (
                      <p className="m-0 mt-2">{msg.text}</p>
                    )}
                  </div>
                </div>
              ))}
              {waiting && (
                <div className={styles.aiMsg}>
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      <i>
                        <img src={AI_AVATAR} alt="avatar" />
                      </i>

                      <span>"AI"</span>
                    </div>

                    <ResponseLoader />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <hr className="m-0" />
            <div className={styles.inputRow}>
              <textarea
                ref={inputRef}
                className={styles.textarea}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !waiting) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Train AI...."
                disabled={waiting}
                rows={1}
                style={{ height: textareaHeight }}
              />
              <div className={styles.bottomBar}>
                {/* {!summary && from !== "reviewSession" && (
                  <div className={styles.bottomIconWrap}>
                    <Plus className={styles.bottomIcon} />
                  </div>
                )} */}

                {!summary && from !== "reviewSession" && (
                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("summary")}
                    onMouseLeave={() => setHovered("")}
                    onClick={handleGenerateSummary}
                  >
                    <FileText className={styles.bottomIcon} />
                    <span
                      className={
                        hovered === "summary"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      Generate Summary
                    </span>
                  </div>
                )}
                {from === "reviewSession" && (
                  <span className={styles.bottomIconTime}>{timer}</span>
                )}
                <div
                  onClick={() => {
                    if (waiting || !input.trim()) return;
                    handleSend();
                  }}
                  className={
                    styles.bottomIconWrap +
                    (waiting || !input.trim() ? " " + styles.disabled : "")
                  }
                >
                  <ArrowUp className={styles.bottomIcon} />
                </div>

                {from === "reviewSession" && (
                  <div
                    className={styles.bottomIconWrapDanger}
                    onMouseEnter={() => setHovered("endSession")}
                    onMouseLeave={() => setHovered("")}
                  >
                    <PhoneOff className={styles.bottomIcon} />
                    <span
                      className={
                        hovered === "endSession"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      End Session
                    </span>
                  </div>
                )}
                {summary && from !== "reviewSession" && (
                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("summary")}
                    onMouseLeave={() => setHovered("")}
                    
                  >
                    <RotateCcw className={styles.bottomIcon} />
                    <span
                      className={
                        hovered === "summary"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      Regenerate Summary
                    </span>
                  </div>
                )}
                {from !== "reviewSession" && (
                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("save")}
                    onMouseLeave={() => setHovered("")}
                    onClick={handleOpenSaveSession}
                  >
                    <AudioLines className={styles.bottomIcon} />
                    <span
                      className={
                        hovered === "save"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      Save Session
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showSaveSession &&
        createPortal(
          <SessionActions
            show={showSaveSession}
            onHide={() => setShowSaveSession(false)}
            generateSummary={handleGenerateSummary}
            saveDraft={() => handleFinalizeSession(0, 1)}
            publishSession={() => handleFinalizeSession(1, 0)}
            saveSession={() => handleFinalizeSession(0, 1)}
            summary={summary}
          />,
          document.body
        )}
    </>
  );
};

export default ChatPopup;
